import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  Zap, Calculator, Search, Wrench, MessageSquare,
  LayoutDashboard, BarChart3, Scale, Briefcase,
  FileText, Sparkles, HardHat, Users, FileSignature,
  Building2, UsersRound, ChevronLeft, ChevronRight,
  Home, Target, TrendingUp,
} from 'lucide-react';

const sidebarSections = [
  {
    label: 'Tools',
    items: [
      { path: '/analyzer', label: 'Deal Analyzer', icon: Calculator },
      { path: '/comps', label: 'Find Comps', icon: Search },
      { path: '/repair-estimator', label: 'Repair Costs', icon: Wrench },
      { path: '/chat', label: 'AI Assistant', icon: MessageSquare },
    ],
  },
  {
    label: 'Manage',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: Home },
      { path: '/my-deals', label: 'My Deals', icon: LayoutDashboard },
      { path: '/pipeline', label: 'Pipeline', icon: Target },
      { path: '/compare', label: 'Compare Deals', icon: Scale },
      { path: '/projects', label: 'Projects', icon: Briefcase },
      { path: '/analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Market',
    items: [
      { path: '/marketing', label: 'Deal Marketing', icon: Sparkles },
      { path: '/wholesale', label: 'Wholesale Hub', icon: TrendingUp },
    ],
  },
  {
    label: 'Finance',
    items: [
      { path: '/funding', label: 'Lender Funding', icon: FileText },
      { path: '/contracts', label: 'Contracts', icon: Scale },
      { path: '/esign', label: 'E-Sign', icon: FileSignature },
    ],
  },
  {
    label: 'Network',
    items: [
      { path: '/crm', label: 'Investor CRM', icon: Users },
      { path: '/contractors', label: 'Contractors', icon: HardHat },
      { path: '/teams', label: 'Teams', icon: UsersRound },
      { path: '/directory', label: 'Directory', icon: Building2 },
    ],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();

  if (!isAuthenticated) return null;

  // Hide sidebar on public-only pages
  const publicPaths = ['/', '/login', '/register', '/forgot-password', '/pricing', '/blog', '/help', '/about', '/contact', '/careers', '/partners', '/privacy', '/terms', '/cookies'];
  const isPublicPage = publicPaths.includes(location.pathname) || location.pathname.startsWith('/blog/') || location.pathname.startsWith('/esign/sign/') || location.pathname.startsWith('/shared/') || location.pathname.startsWith('/pitch-deck/s/') || location.pathname.startsWith('/invite/');
  if (isPublicPage) return null;

  const isActive = (path) => location.pathname === path ||
    (path === '/comps' && location.pathname === '/comp-finder');

  return (
    <aside
      className={`fixed top-16 left-0 bottom-0 z-40 flex flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-all duration-200 ${
        collapsed ? 'w-[60px]' : 'w-[210px]'
      }`}
      data-testid="app-sidebar"
    >
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-2 sidebar-scroll">
        {sidebarSections.map((section, idx) => (
          <div key={section.label}>
            {!collapsed && (
              <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 select-none">
                {section.label}
              </div>
            )}
            {collapsed && idx > 0 && <div className="my-1.5 mx-3 border-t border-gray-100 dark:border-gray-800" />}
            {section.items.map((item) => {
              const active = isActive(item.path);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={collapsed ? item.label : undefined}
                  data-testid={`sidebar-${item.path.slice(1)}`}
                  className={`flex items-center gap-2.5 mx-1.5 px-2.5 py-[7px] rounded-md text-[13px] transition-colors ${
                    active
                      ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200'
                  } ${collapsed ? 'justify-center px-0' : ''}`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-purple-600 dark:text-purple-400' : ''}`} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center justify-center h-9 border-t border-gray-200 dark:border-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors flex-shrink-0"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
    </aside>
  );
};

export default Sidebar;
